// Workflow Automation Constants

export const TRIGGER_EVENTS = [
  { id: 'lead_created', name: 'Lead Created', description: 'Triggers when a new lead is created', category: 'Lead', icon: 'UserPlus' },
  { id: 'lead_updated', name: 'Lead Updated', description: 'Triggers when any lead field is updated', category: 'Lead', icon: 'Edit' },
  { id: 'lead_closed_won', name: 'Lead Closed WON', description: 'Triggers when lead stage changes to CLOSED WON', category: 'Lead', icon: 'Trophy' },
  { id: 'lead_closed_lost', name: 'Lead Closed LOST', description: 'Triggers when lead stage changes to CLOSED LOST', category: 'Lead', icon: 'XCircle' },
  { id: 'ota_listing_completed', name: 'OTA Listing Completed', description: 'Triggers when any OTA listing status becomes COMPLETED', category: 'OTA', icon: 'CheckCircle' },
  { id: 'ota_status_changed', name: 'OTA Status Changed', description: 'Triggers when any OTA status changes', category: 'OTA', icon: 'RefreshCw' },
  { id: 'followup_completed', name: 'Follow-up Completed', description: 'Triggers when a follow-up is marked complete', category: 'Activity', icon: 'Phone' },
  { id: 'payment_completed', name: 'Payment Completed', description: 'Triggers when payment status changes to Completed', category: 'Payment', icon: 'DollarSign' },
  { id: 'task_completed', name: 'Task Completed', description: 'Triggers when a task is marked as completed', category: 'Task', icon: 'CheckSquare' },
  { id: 'document_checklist_completed', name: 'Document Checklist Completed', description: 'Triggers when all document checklist items are complete', category: 'Documents', icon: 'FileCheck' },
  { id: 'pending_document_reminder', name: 'Pending Document Reminder', description: "Triggers when one or more property checklist documents transition to 'Pending'. Variable {{lead.pending_documents}} lists every pending doc.", category: 'Documents', icon: 'FileClock' },
  { id: 'property_ready', name: 'Property Ready', description: 'Triggers when property status changes to Active', category: 'Property', icon: 'Building' },
  { id: 'custom', name: 'Custom Trigger', description: 'Custom webhook-based trigger', category: 'Custom', icon: 'Webhook' },
];

export const ACTION_TYPES = [
  { id: 'send_email', name: 'Send Email', description: 'Send an email to the lead', icon: 'Mail', color: 'bg-blue-500' },
  { id: 'send_sms', name: 'Send SMS', description: 'Send an SMS message', icon: 'MessageSquare', color: 'bg-green-500' },
  { id: 'send_whatsapp', name: 'Send WhatsApp', description: 'Send a WhatsApp message', icon: 'MessageCircle', color: 'bg-emerald-500' },
  { id: 'send_webhook', name: 'Send Webhook', description: 'Send data to external URL', icon: 'Globe', color: 'bg-purple-500' },
  { id: 'update_lead', name: 'Update Lead', description: 'Update lead field values', icon: 'Edit', color: 'bg-amber-500' },
  { id: 'create_task', name: 'Create Task', description: 'Create a new task', icon: 'CheckSquare', color: 'bg-teal-500' },
  { id: 'assign_lead', name: 'Assign Lead', description: 'Assign lead to a user', icon: 'UserPlus', color: 'bg-indigo-500' },
  { id: 'send_notification', name: 'Send Notification', description: 'Send in-app notification', icon: 'Bell', color: 'bg-pink-500' },
];

export const CONDITION_OPERATORS = [
  { id: 'equals', name: 'Equals', symbol: '=' },
  { id: 'not_equals', name: 'Not Equals', symbol: '≠' },
  { id: 'contains', name: 'Contains', symbol: '∋' },
  { id: 'not_contains', name: 'Does Not Contain', symbol: '∌' },
  { id: 'starts_with', name: 'Starts With', symbol: '^' },
  { id: 'ends_with', name: 'Ends With', symbol: '$' },
  { id: 'greater_than', name: 'Greater Than', symbol: '>' },
  { id: 'less_than', name: 'Less Than', symbol: '<' },
  { id: 'greater_than_or_equal', name: 'Greater Than or Equal', symbol: '≥' },
  { id: 'less_than_or_equal', name: 'Less Than or Equal', symbol: '≤' },
  { id: 'is_empty', name: 'Is Empty', symbol: '∅' },
  { id: 'is_not_empty', name: 'Is Not Empty', symbol: '≠∅' },
  { id: 'in_list', name: 'In List', symbol: '∈' },
  { id: 'not_in_list', name: 'Not In List', symbol: '∉' },
];

export const CONDITION_FIELDS = [
  { field: 'lead_stage', label: 'Lead Stage', type: 'select', options: ['NEW', 'NOT CONNECTED', 'CONTACTED', 'HOT', 'PROPOSAL SENT', 'FOLLOW UP', 'TERMINATED', 'CLOSED WON', 'CLOSED LOST'] },
  { field: 'property_status', label: 'Property Status', type: 'select', options: ['Active', 'Inactive'] },
  { field: 'source', label: 'Lead Source', type: 'select', options: ['GRB Data', 'Incoming Call', 'WhatsApp', 'Website', 'Facebook Ad', 'Google Ad', 'Indiamart', 'Tradeindia', 'Justdial', 'Sulekha', 'Paper Ad', 'Cold Calling', 'Reference'] },
  { field: 'state', label: 'State', type: 'text' },
  { field: 'city', label: 'City', type: 'text' },
  { field: 'website_available', label: 'Website Available', type: 'select', options: ['Yes', 'No'] },
  { field: 'existing_ota_connected', label: 'Existing OTA', type: 'select', options: ['Online', 'Offline', 'Partially Online'] },
  { field: 'ota_info.ota_name', label: 'OTA Name', type: 'select', options: ['MMT', 'Agoda', 'Expedia', 'Airbnb', 'Booking_Com', 'Cleartrip', 'EasemyTrip', 'Goibibo', 'Tripadvisor'] },
  { field: 'ota_info.ota_status', label: 'OTA Status', type: 'select', options: ['INTEGRATION', 'NOT STARTED', 'PROGRESS', 'SENT TO QC', 'COMPLETED'] },
  { field: 'ota_info.access_to_hotel', label: 'OTA Access', type: 'select', options: ['Granted', 'Not Granted'] },
  { field: 'payment_info.interested_plan', label: 'Interested Plan', type: 'select', options: ['Silver', 'Gold', 'Platinum'] },
  { field: 'payment_info.setup_payment_status', label: 'Payment Status', type: 'select', options: ['Pending', 'Completed'] },
  { field: 'property_checklist_info.master_form', label: 'Master Form', type: 'select', options: ['N/A', 'Complete', 'Pending'] },
  { field: 'property_checklist_info.document_collection_status', label: 'Doc Collection Status', type: 'select', options: ['N/A', 'Complete', 'Pending'] },
  { field: 'no_of_rooms', label: 'Number of Rooms', type: 'number' },
  { field: 'lead_owner', label: 'Lead Owner', type: 'text' },
];

export const TEMPLATE_VARIABLES = [
  { name: 'lead.hotelName', description: 'Hotel/Property Name', sample: 'Grand Plaza Hotel', category: 'Lead' },
  { name: 'lead.ownerName', description: 'Owner Name', sample: 'John Doe', category: 'Lead' },
  { name: 'lead.mobileNumber', description: 'Mobile Number', sample: '+919876543210', category: 'Lead' },
  { name: 'lead.email', description: 'Email Address', sample: 'john@example.com', category: 'Lead' },
  { name: 'lead.city', description: 'City', sample: 'Mumbai', category: 'Lead' },
  { name: 'lead.state', description: 'State', sample: 'Maharashtra', category: 'Lead' },
  { name: 'lead.leadStage', description: 'Lead Stage', sample: 'HOT', category: 'Lead' },
  { name: 'lead.leadSource', description: 'Lead Source', sample: 'Website', category: 'Lead' },
  { name: 'lead.propertyStatus', description: 'Property Status', sample: 'Active', category: 'Lead' },
  { name: 'lead.salesOwner', description: 'Sales Owner/Agent', sample: 'Admin', category: 'Lead' },
  { name: 'lead.noOfRooms', description: 'Number of Rooms', sample: '50', category: 'Lead' },
  { name: 'ota.otaName', description: 'OTA Name', sample: 'MMT', category: 'OTA' },
  { name: 'ota.otaStatus', description: 'OTA Status', sample: 'COMPLETED', category: 'OTA' },
  { name: 'ota.hotelId', description: 'OTA Hotel ID', sample: 'MMT123456', category: 'OTA' },
  { name: 'ota.listingUrl', description: 'OTA Listing URL', sample: 'https://makemytrip.com/...', category: 'OTA' },
  { name: 'payment.interestedPlan', description: 'Interested Plan', sample: 'Gold', category: 'Payment' },
  { name: 'payment.setupPaymentStatus', description: 'Setup Payment Status', sample: 'Completed', category: 'Payment' },
  { name: 'payment.pendingPayment', description: 'Pending Payment Amount', sample: '₹5,000', category: 'Payment' },
  { name: 'workflow.name', description: 'Workflow Name', sample: 'OTA Completion Alert', category: 'Workflow' },
  { name: 'currentDate', description: 'Current Date', sample: '2026-02-17', category: 'System' },
  { name: 'currentTime', description: 'Current Time', sample: '10:30 AM', category: 'System' },
];

export const WORKFLOW_STATUSES = [
  { id: 'active', name: 'Active', color: 'bg-green-100 text-green-800' },
  { id: 'inactive', name: 'Inactive', color: 'bg-gray-100 text-gray-800' },
  { id: 'draft', name: 'Draft', color: 'bg-yellow-100 text-yellow-800' },
];

export const SCHEDULE_TYPES = [
  { id: 'immediate', name: 'Immediately', description: 'Run as soon as triggered' },
  { id: 'delayed', name: 'After Delay', description: 'Run after a specified delay' },
  { id: 'scheduled', name: 'Scheduled', description: 'Run at a specific date/time' },
  { id: 'recurring', name: 'Recurring', description: 'Run on a recurring schedule' },
];

export const DELAY_UNITS = [
  { id: 'minutes', name: 'Minutes', multiplier: 1 },
  { id: 'hours', name: 'Hours', multiplier: 60 },
  { id: 'days', name: 'Days', multiplier: 1440 },
];

export const RECURRING_FREQUENCIES = [
  { id: 'daily', name: 'Daily', description: 'Every day at the selected time' },
  { id: 'weekly', name: 'Weekly', description: 'Once a week (same weekday as start date)' },
  { id: 'monthly', name: 'Monthly', description: 'Once a month (same day-of-month as start date)' },
];

// Helper function to get empty workflow
export const getEmptyWorkflow = () => ({
  name: '',
  description: '',
  trigger_event: '',
  trigger_config: {},
  conditions: {
    logic: 'and',
    rules: [],
    groups: []
  },
  actions: [],
  status: 'draft',
  schedule: {
    type: 'immediate',
    delay_minutes: null,
    cron_expression: null,
    start_date: null,
    end_date: null,
    time: null,
    frequency: 'daily',
    timezone: 'UTC',
    run_limit: null
  },
  tags: [],
  segment_id: null
});

// Helper function to get empty action
export const getEmptyAction = (type = 'send_email') => ({
  type,
  template_id: null,
  delay_minutes: 0,
  priority: 1,
  retry_on_fail: true,
  max_retries: 3,
  config: {}
});

// Helper function to get empty condition rule
export const getEmptyConditionRule = () => ({
  field: '',
  operator: 'equals',
  value: ''
});

// Helper function to get empty condition group
export const getEmptyConditionGroup = () => ({
  logic: 'and',
  rules: [],
  groups: []
});
